import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { TemplatePortal } from '@angular/cdk/portal';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { MatLegacyButton as MatButton } from '@angular/material/legacy-button';

import { TsButtonComponent } from 'tsui';
import { SelectableFieldDef } from './column-selector.types';

@Component({
  selector: 'app-column-selector',
  templateUrl: './column-selector.component.html',
  styleUrls: ['./column-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnSelectorComponent implements OnInit {
  @Input() draggable: boolean = false;

  @Input() set selectedOrder(value: string[]) {
    this.setupItems(value);
  }

  @Input() set items(value: SelectableFieldDef[]) {
    this._items = value || [];
    const selectedItems = this._items?.filter(({ selected }) => selected);
    this.setupItems(selectedItems.map(({ name }) => name));
    this.selectedItemsCount = selectedItems.length;
  }

  @Input() position: ConnectedPosition = { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' };
  @Input() icon: { name: string; isSvg: boolean } = { name: 'visibility', isSvg: true };
  @Input() showCountBadge = false;
  @Input() size = null;
  @Output() onSave: EventEmitter<SelectableFieldDef[]> = new EventEmitter<SelectableFieldDef[]>();
  @Input() isButton = false; // Use this to change the CTA appearance. If false, it will be an icon.
  @Input() disabled = false;

  private optionsPanelOverlayRef: OverlayRef;
  private optionsPanelTemplatePortal: TemplatePortal;
  @ViewChild('optionsPanel') private optionsPanel: TemplateRef<any>;
  @ViewChild('optionsPanelOrigin') private optionsPanelOrigin: MatButton | TsButtonComponent;

  filterText: string;
  selectedItemsCount = 0;

  private _items: SelectableFieldDef[] = [];
  private snapshot: string[];

  get canSave(): boolean {
    const keptSameItems = this.getSortedString(this.selectedItemsNames) === this.getSortedString(this.snapshot);

    if (this.draggable) {
      const keptSameOrder = JSON.stringify(this.selectedItemsNames) === JSON.stringify(this.snapshot);
      return !keptSameItems || !keptSameOrder;
    }

    return !keptSameItems;
  }

  get items(): SelectableFieldDef[] {
    return this._items;
  }

  private get selectedItemsNames(): string[] {
    return this.items?.filter(({ selected }) => selected).map(({ name }) => name);
  }

  constructor(
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef,
  ) {}

  ngOnInit(): void {}

  checked(option: SelectableFieldDef): void {
    option.selected = !option.selected;
  }

  save(): void {
    const selectedItems = this.items.filter(({ selected }) => selected);
    this.onSave.emit(selectedItems);
    this.selectedItemsCount = selectedItems.length;
    this.closeDisplayFieldsPanel();
  }

  showOptions(): void {
    this.snapshot = this.selectedItemsNames;

    const isTsButtonOrigin =
      this.optionsPanelOrigin instanceof TsButtonComponent
        ? this.optionsPanelOrigin.viewContainerRef.element.nativeElement
        : this.optionsPanelOrigin._elementRef.nativeElement;

    this.optionsPanelOverlayRef = this.overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay.position().flexibleConnectedTo(isTsButtonOrigin).withPositions([this.position]),
    });
    this.optionsPanelTemplatePortal = new TemplatePortal(this.optionsPanel, this.viewContainerRef);
    this.optionsPanelOverlayRef.attach(this.optionsPanelTemplatePortal);
    this.optionsPanelOverlayRef.backdropClick().subscribe(() => this.closeDisplayFieldsPanel(true));
  }

  itemsTrackBy(index: number, name: string): string {
    return name;
  }

  dropped(event: CdkDragDrop<SelectableFieldDef[]>): void {
    moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
  }

  private closeDisplayFieldsPanel(keepSnapshot = false): void {
    if (this.optionsPanelOverlayRef && this.optionsPanelOverlayRef.hasAttached()) {
      this.optionsPanelOverlayRef.detach();
    }

    if (this.optionsPanelTemplatePortal && this.optionsPanelTemplatePortal.isAttached) {
      this.optionsPanelTemplatePortal.detach();
    }

    if (keepSnapshot) {
      this.items = this.items.map((item) => ({
        ...item,
        selected: this.snapshot.filter((snapshotItem) => snapshotItem === item.name).length > 0,
      }));
    }

    this.filterText = null;
  }

  private getSortedString(value: string[]): string {
    return JSON.stringify([...value].sort());
  }

  private setupItems(selectedItems: string[]): void {
    this._items = [
      ...selectedItems.map((name) => this._items.find((x) => x.name === name)).filter((x) => x),
      ...this._items.filter(({ selected }) => !selected),
    ];

    this.snapshot = this.selectedItemsNames;
  }

  getDataTestId(name: string): string {
    return `column-selector-${name.toLowerCase().replace(/ /g, '-')}`;
  }
}
